import { useBreakpoints } from "~/composables/window";
import { useElementBounding } from "@vueuse/core";

const VIRTUALIZE_BUFFER = 500;
const ITEM_INDEX_BUFFER = 3;
const ITEM_GAP_DESKTOP = 16;
const ITEM_GAP_MOBILE = 0;

export type VirtualElement = {
  id: string;
  offset: number;
  size: number;
  loading: boolean;
};

export const useVirtualizedList = <T extends VirtualElement>(
  wrapper: Ref<HTMLElement | undefined>,
  scrollArea: MaybeRef<Document | HTMLElement>,
  onBottomReached = (): any => {},
  onTopReached = (): any => {},
) => {
  const itemList = ref<T[]>([]);

  const { sm } = useBreakpoints();
  const scrollPos = ref<number>(0);
  const scrollOffset = ref<number>(0);
  const { height: wrapperHeight, top: wrapperTop } =
    useElementBounding(wrapper);
  let currAnimFrame: number | undefined;

  const topIndex = ref(0);
  const bottomIndex = ref(0);

  let isAlreadyNearTheTop = false;
  let isAlreadyNearTheBottom = false;

  const updateItemHeight = (index: number, height: number) => {
    const item = itemList.value.at(index);
    if (!item) return;

    const previousItem = index === 0 ? undefined : itemList.value.at(index - 1);
    const previousItemHeight = item.size;
    if (previousItemHeight === height) return;
    if (
      previousItemHeight &&
      item.offset + previousItemHeight < scrollPos.value
    )
      window.scrollBy({ top: height - previousItemHeight });

    item.size = height;
    const dynamicGap = sm.value ? ITEM_GAP_DESKTOP : ITEM_GAP_MOBILE;

    let heightAccumulator = previousItem
      ? previousItem.offset + previousItem.size + dynamicGap
      : 0;

    for (let i = index; i < itemList.value.length; i++) {
      const nextItem = itemList.value[i];
      nextItem.offset = heightAccumulator;
      heightAccumulator += nextItem.size + dynamicGap;
    }

    if (wrapper.value) {
      const lastItem = itemList.value[Math.max(itemList.value.length - 1, 0)];
      wrapper.value.style.height = lastItem
        ? `${lastItem.offset + lastItem.size}px`
        : "";
    }
  };

  const getTopVisibleItemIndex = (): number => {
    if (itemList.value.at(0)?.offset ?? 0 < scrollPos.value) return 0;
    let iterations = 0;

    // binary search over our items
    let left = 0,
      right = itemList.value.length - 1,
      mid = left + Math.floor((right - left) / 2);

    while (right >= left) {
      mid = left + Math.floor((right - left) / 2);
      const itemAtMid = itemList.value[mid];
      const upperCutoff = scrollPos.value - VIRTUALIZE_BUFFER;

      if (
        itemAtMid.offset < upperCutoff &&
        itemAtMid.offset + itemAtMid.size > upperCutoff
      )
        return mid;

      if (itemAtMid.offset > upperCutoff) right = mid - 1;
      if (itemAtMid.offset + itemAtMid.size < upperCutoff) left = mid + 1;

      iterations++;
      if (iterations > 20) return mid;
    }

    return mid;
  };

  const updateVirtualizedItems = () => {
    topIndex.value = getTopVisibleItemIndex();
    const lowerCutoff =
      scrollPos.value + wrapperHeight.value + VIRTUALIZE_BUFFER;
    const foundIndex = itemList.value.findIndex(
      (item) => item.offset > lowerCutoff,
    );
    bottomIndex.value = foundIndex < 0 ? itemList.value.length : foundIndex;
  };

  const deleteItem = (index: number) => {
    const deleted = itemList.value.splice(index, 1);
    const deadzone = sm.value ? ITEM_GAP_DESKTOP : ITEM_GAP_MOBILE;
    itemList.value.slice(index).forEach((_, idx) => {
      const item = itemList.value[index + idx];
      item.offset = item.offset - deleted[0].size - deadzone;
    });
  };

  const getScrollAreaElement = () => {
    const rawScrollArea = unref(scrollArea);
    return rawScrollArea instanceof Document
      ? rawScrollArea.documentElement
      : rawScrollArea;
  };

  const onScrollHandler = () => {
    const { clientHeight, scrollTop, scrollHeight } = getScrollAreaElement();

    const itemGap = sm.value ? ITEM_GAP_DESKTOP : ITEM_GAP_MOBILE;
    scrollPos.value = scrollTop - scrollOffset.value;
    const skeletonItems = itemList.value.filter((a) => a.loading);
    const skeletonHeight =
      skeletonItems.reduce((a, b) => a + b.size + itemGap, 0) +
      VIRTUALIZE_BUFFER;

    const isNearTheTop =
      skeletonItems.length > 0 &&
      scrollPos.value + clientHeight > scrollHeight - skeletonHeight;
    const isNearTheBottom =
      skeletonItems.length > 0 && scrollPos.value < skeletonHeight;

    if (isNearTheTop) {
      if (!isAlreadyNearTheBottom) onBottomReached();
      isAlreadyNearTheBottom = true;
    } else {
      isAlreadyNearTheBottom = false;
    }

    if (isNearTheBottom) {
      if (!isAlreadyNearTheTop) onTopReached();
      isAlreadyNearTheTop = true;
    } else {
      isAlreadyNearTheTop = false;
    }

    updateVirtualizedItems();
  };

  const onScroll = () => {
    if (currAnimFrame) {
      cancelAnimationFrame(currAnimFrame);
      currAnimFrame = undefined;
    }
    currAnimFrame = requestAnimationFrame(onScrollHandler);
  };

  const onResize = () => {
    const scrollAreaElement = getScrollAreaElement();
    if (wrapper.value)
      scrollOffset.value =
        wrapperTop.value - scrollAreaElement.getBoundingClientRect().top;
  };

  const addListeners = () => {
    unref(scrollArea).addEventListener("scroll", onScroll);
    document.addEventListener("resize", onResize);
    onResize();
    onScrollHandler();
  };

  const removeListeners = () => {
    unref(scrollArea).removeEventListener("scroll", onScroll);
    document.removeEventListener("resize", onResize);
  };

  const visibleItemList = computed(() =>
    itemList.value.slice(
      Math.max(topIndex.value - ITEM_INDEX_BUFFER, 0),
      Math.min(bottomIndex.value + ITEM_INDEX_BUFFER, itemList.value.length),
    ),
  );

  onMounted(addListeners);
  onBeforeUnmount(removeListeners);

  return {
    updateItemHeight,
    deleteItem,
    itemList,
    visibleItemList,
    indexOffset: computed(() =>
      Math.max(topIndex.value - ITEM_INDEX_BUFFER, 0),
    ),
  };
};
